/**
 * Membership Success Page - Olympic Fit Wellness
 * Handles Stripe checkout success redirect
 */
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, ArrowRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MembershipSuccessPage() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    } else {
      setStatus('error');
      setError('No session ID found');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      setError('Payment verification timed out. Please check your email for confirmation.');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/status/${sessionId}`);
      const data = await response.json();

      if (data.payment_status === 'paid') {
        setPaymentData(data);
        setStatus('success');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        setError('Payment session expired. Please try again.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking payment status:', err);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="membership-success-page">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-neutral-400 animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-heading font-bold text-neutral-900 mb-2">
              Processing Payment...
            </h1>
            <p className="text-neutral-500">
              Please wait while we confirm your membership.
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-neutral-900 mb-2">
              Welcome to the Family!
            </h1>
            <p className="text-neutral-500 mb-6">
              Your membership has been activated successfully.
            </p>

            {paymentData && (
              <div className="bg-neutral-50 rounded-xl p-4 mb-6 text-left">
                <div className="flex justify-between mb-2">
                  <span className="text-neutral-500">Amount Paid</span>
                  <span className="font-bold text-neutral-900">
                    ${paymentData.amount?.toFixed(2)} {paymentData.currency?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Status</span>
                  <span className="text-green-600 font-semibold">Active</span>
                </div>
              </div>
            )}

            <div className="space-y-3">
              <Link 
                to="/portal" 
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                Go to Member Portal
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link 
                to="/portal/schedule" 
                className="w-full inline-block text-center text-neutral-600 hover:text-neutral-800 font-medium"
              >
                Book Your First Class
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-heading font-bold text-neutral-900 mb-2">
              Something Went Wrong
            </h1>
            <p className="text-neutral-500 mb-6">
              {error || 'We couldn\'t verify your payment. Please contact support.'}
            </p>
            <div className="space-y-3">
              <Link 
                to="/membership" 
                className="btn-primary w-full"
              >
                Try Again
              </Link>
              <Link 
                to="/contact" 
                className="w-full inline-block text-center text-neutral-600 hover:text-neutral-800 font-medium"
              >
                Contact Support
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
